import {
    clearAllBodyScrollLocks,
    disableBodyScroll,
    enableBodyScroll,
} from "body-scroll-lock";
import { Immutable } from "immer";
import React from "react";
import SVG from "react-inlinesvg";
import { CSSTransition } from "react-transition-group";
import { t } from "ttag";

import { getDocument, getWindow } from "@thelabnyc/thelabui/src/utils/dom";

import iconArrowLeftBlueAlternate from "../../../../img/icons/arrow-left-blue--alternate.svg";
import {
    UncontrolledPopover,
    UncontrolledPopoverRef,
} from "../../../components/Popover";
import { RawHTML } from "../../../components/RawHTML";
import { useAppSelector } from "../../../store/reducers";
import { PriceType, getProductPrice } from "../../../utils/catalogue";
import { format } from "../../../utils/format";
import { focusElement } from "../../../utils/keyboardFocus";
import { MOBILE_WIDTH_THRESHOLD_ALTERNATIVE } from "../constants";
import { defaults } from "../defaults";
import { IPLCInfoPanel } from "../models";
import { upgradedVariantPriceSelector } from "../selectors";

// Load styles
import "./index.scss";

const BEM_NAME = "plc-info-panel";

const getParentElement = (): HTMLElement => {
    const document = getDocument();
    const element = document?.querySelector(
        ".plc-configurator",
    ) as HTMLElement | null;
    // Return a fallback element if not found (shouldn't happen in practice)
    return element || document?.body || ({} as HTMLElement);
};

const handleBodyScroll = (
    isOpen: boolean,
    popoverDivRef: React.RefObject<HTMLDivElement | null>,
): void => {
    if (!popoverDivRef.current) return;

    const window = getWindow();
    if ((window?.innerWidth ?? 0) < MOBILE_WIDTH_THRESHOLD_ALTERNATIVE) {
        if (isOpen) {
            disableBodyScroll(popoverDivRef.current);
        } else {
            enableBodyScroll(popoverDivRef.current);
        }
    } else {
        enableBodyScroll(popoverDivRef.current);
    }
};

export const PLCInfoPanel = (
    props: Immutable<{
        buttonText: string | React.ReactNode;
        content: IPLCInfoPanel;
        onAfterOpen?: () => void;
    }>,
): React.ReactNode => {
    const [isOpen, setIsOpen] = React.useState(false);
    const closeBtnElem = React.useRef<HTMLButtonElement>(null);
    const popover = React.useRef<UncontrolledPopoverRef>(null);
    const popoverDiv = React.useRef<HTMLDivElement | null>(null);
    const prevIsOpenRef = React.useRef(false);

    const priceFromState = useAppSelector((rootState) => {
        const state = rootState.configurator || defaults;
        return upgradedVariantPriceSelector(state);
    });

    const afterOpenCallback = props.onAfterOpen;

    const handlePopoverAfterOpen = () => {
        setIsOpen(true);
    };

    const onRequestClose = () => {
        // `enableBodyScroll` should run before setState `isPanelOpen: false`,
        // so inline style `position: fixed` from `<body>` will be removed first before removing `.configurator-panel-open` from `<body>`.
        // This way can avoid the issue of the page jumping to the top when panel is opened/closed
        enableBodyScroll(popoverDiv.current as HTMLElement);
        setIsOpen(false);
    };

    React.useEffect(() => {
        if (isOpen) {
            handleBodyScroll(isOpen, popoverDiv);

            if (closeBtnElem.current) {
                focusElement(closeBtnElem.current);
            }
            if (afterOpenCallback) {
                afterOpenCallback();
            }
        }
    }, [isOpen, afterOpenCallback]);

    React.useEffect(() => {
        if (prevIsOpenRef.current && !isOpen && popover.current) {
            const timeoutId = setTimeout(() => {
                if (popover.current) {
                    popover.current.requestClose();
                }
            }, 400);
            return () => clearTimeout(timeoutId);
        }
    }, [isOpen]);

    React.useEffect(() => {
        prevIsOpenRef.current = isOpen;
    }, [isOpen]);

    React.useEffect(() => {
        return () => {
            clearAllBodyScrollLocks();
        };
    }, []);

    const price = priceFromState
        ? getProductPrice(priceFromState, {
              priceType: PriceType.COSMETIC_EXCL_TAX,
              includePostDiscountAddons: true,
              quantity: priceFromState.quantity,
          })
        : null;

    return (
        <span className={BEM_NAME}>
            <UncontrolledPopover
                ref={popover}
                triggerContent={props.buttonText}
                triggerBtnProps={{
                    "aria-label": t`Learn More`,
                    "className": `${BEM_NAME}__open-button`,
                }}
                popperOptions={{
                    placement: "left",
                }}
                modalProps={{
                    className: `${BEM_NAME}__modal`,
                    overlayClassName: `${BEM_NAME}__overlay`,
                    closeBtnProps: {
                        className: `${BEM_NAME}__close-button`,
                    },
                    disableHeightExpansion: true,
                }}
                disablePopper={true}
                onParentSelector={getParentElement}
                onAfterOpen={handlePopoverAfterOpen}
            >
                <CSSTransition
                    key="plc-info-panel"
                    in={isOpen}
                    timeout={100}
                    classNames="plc-info-panel__content-"
                    unmountOnExit={true}
                    nodeRef={popoverDiv}
                >
                    <div
                        aria-modal="true"
                        className={`${BEM_NAME}__content`}
                        role="dialog"
                        ref={popoverDiv}
                    >
                        <header>
                            <button
                                aria-label="Info Panel - Back"
                                onClick={onRequestClose}
                                ref={closeBtnElem}
                            >
                                <SVG src={iconArrowLeftBlueAlternate} />
                                Back
                            </button>
                            <div className={`${BEM_NAME}__current-price`}>
                                {price ? format.money(price) : ""}
                            </div>
                        </header>
                        <RawHTML html={props.content.body} />
                    </div>
                </CSSTransition>
            </UncontrolledPopover>
        </span>
    );
};
